import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Container, Stack, Typography } from "@mui/material";
import React from "react";
import Breadcrumb from "../Breadcrumb";
import Footer from "./Footer";
import WarningBanner from "../WarningBanner";
import { SiteTranslationSelection } from "@shared/components/site-translation/SiteTranslationSelection";
import useGlobalStore from "@shared/store/GlobalStore";
import { useLocation } from "react-router-dom";
import Header from "./Header";
import { usePageVersion } from "@frontoffice/hooks/PageVersionHook";
import { useFooter, useMenu } from "@shared/hooks/SectionHook";
import { useSites } from "@shared/hooks/SiteHook";
const Layout = (props) => {
    const { children } = props;
    const { isBo } = useGlobalStore((state) => ({ isBo: state.isBo }));
    const { pathname } = useLocation();
    // Scroll to top when the pathname changes
    React.useEffect(() => {
        if (typeof window !== "undefined") {
            window.scrollTo({ top: 0, left: 0, behavior: "auto" });
        }
    }, [pathname]);
    const { currentSite: site, isLoading: isSiteLoading } = useSites();
    const { isLoading: isFooterLoading } = useFooter(site?.id);
    const { isLoading: isMenuLoading } = useMenu(site?.id);
    const isLoading = isSiteLoading || isFooterLoading || isMenuLoading;
    const { currentPageVersion, loading: isPageLoading } = usePageVersion();
    const { title, shortDescription } = currentPageVersion || {};
    const isHomePage = pathname === "/" || currentPageVersion?.path === "/";
    const decodedShortDescription = React.useMemo(() => {
        if (!shortDescription) {
            return shortDescription;
        }
        try {
            return decodeURIComponent(shortDescription);
        }
        catch {
            return shortDescription;
        }
    }, [shortDescription]);
    const shortDescriptionText = decodedShortDescription
        ?.replace(/<[^>]*>/g, " ")
        .replace(/\s+/g, " ")
        .trim();
    if (isLoading)
        return null;
    return (_jsxs(Stack, { sx: {
            minHeight: "100vh",
            overflow: "hidden",
            ...(isBo
                ? {
                    contain: "layout",
                    maxWidth: "100vw",
                }
                : {}),
        }, children: [_jsxs(Stack, { sx: { flex: "1 0 auto" }, children: [_jsxs(Header, { children: [_jsx(WarningBanner, {}), !isBo && !isHomePage && _jsx(SiteTranslationSelection, {})] }), _jsxs(Box, { component: "main", id: "main-content", tabIndex: -1, sx: {
                            pt: isBo ? 0 : { xs: "72px", md: "88px" },
                            // Permet d'éviter le décalage du footer pendant le chargement, solution potentiellement temporaire
                            ...(isPageLoading &&
                                !isBo && {
                                minHeight: "100vh",
                                "@supports (height: 100dvh)": {
                                    minHeight: "100dvh",
                                },
                            }),
                        }, children: [!isHomePage && (_jsxs(Box, { sx: {
                                    mt: isHomePage || !isBo ? 0 : { xs: "72px", md: "88px" },
                                    position: "relative",
                                    py: {
                                        xs: "40px",
                                        md: "64px",
                                    },
                                    overflow: "hidden",
                                    backgroundColor: "background.header",
                                    height: "280px",
                                    "&::before": {
                                        content: '""',
                                        position: "absolute",
                                        background: "linear-gradient(90deg, hsla(240, 100%, 50%, 0.25) 0%, #0000FF 50%, hsla(240, 100%, 50%, 0.25) 100%)",
                                        backgroundPosition: "center",
                                        backgroundRepeat: "no-repeat",
                                        inset: 0,
                                        zIndex: 1,
                                    },
                                }, children: [_jsxs("picture", { children: [_jsx("source", { media: "(max-width: 899px)", srcSet: "/pattern-mobile.svg" }), _jsx(Box, { component: "img", src: "/pattern.svg", alt: "", loading: "eager", fetchPriority: "high", sx: {
                                                    position: "absolute",
                                                    inset: 0,
                                                    width: "100%",
                                                    height: "100%",
                                                    objectFit: "cover",
                                                    objectPosition: "center",
                                                    zIndex: 0,
                                                } })] }), _jsx(Container, { sx: {
                                            position: "relative",
                                            zIndex: 2,
                                            maxWidth: "1208px !important",
                                            "& *": {
                                                color: "primary.contrastText",
                                            },
                                        }, children: _jsxs(Stack, { spacing: 2, children: [_jsx(Breadcrumb, {}), _jsxs(Stack, { spacing: 3, children: [_jsx(Typography, { variant: "h1", sx: { color: "primary.contrastText" }, children: title }), shortDescriptionText && (_jsx(Typography, { variant: "subtitle1", component: "p", sx: {
                                                                color: "primary.contrastText",
                                                                maxWidth: "600px",
                                                                fontWeight: 400,
                                                            }, children: shortDescriptionText }))] })] }) })] })), _jsx(Box, { children: children })] })] }), _jsx(Footer, {})] }));
};
export default Layout;
